import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/data/images";
import { ArrowDownRight, ArrowRight } from "lucide-react";

export const Hero = () => {
  const { t } = useLanguage();
  const s = t.scuba;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section ref={ref} className="relative h-screen min-h-[720px] w-full overflow-hidden bg-ocean-deep flex flex-col justify-end">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.scuba.hero} alt="" className="h-full w-full object-cover brightness-110 contrast-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-ocean-deep via-ocean-deep/45 to-ocean-deep/15" />
      </motion.div>

      <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-16 md:pb-24 max-w-6xl">
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-mono text-cyan-300 text-xs md:text-sm tracking-[0.25em] mb-6"
          data-testid="scuba-hero-eyebrow"
        >
          {s.hero.eyebrow}
        </motion.p>
        <h1 className="font-syne text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-white mb-8" data-testid="scuba-hero-title">
          {s.hero.title.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.15 * i + 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="font-manrope text-base md:text-lg text-white/70 max-w-xl mb-10"
          data-testid="scuba-hero-subtitle"
        >
          {s.hero.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="flex flex-wrap gap-4"
        >
          <a
            href="#contact"
            data-testid="scuba-hero-cta-primary"
            className="group inline-flex items-center gap-2 bg-white text-ocean-deep rounded-full px-7 py-3.5 font-manrope font-semibold text-sm hover:bg-cyan-200 transition-colors duration-300"
          >
            {s.hero.ctaPrimary}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#services"
            data-testid="scuba-hero-cta-secondary"
            className="group inline-flex items-center gap-2 border border-white/30 text-white rounded-full px-7 py-3.5 font-manrope font-semibold text-sm hover:border-cyan-300 hover:text-cyan-300 transition-colors duration-300"
          >
            {s.hero.ctaSecondary}
            <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
